import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { Email } from '@/store/emailStore';
import { authService } from '@/services/auth';
import toast from 'react-hot-toast';

// Create axios instance
const api = axios.create({
  baseURL: authService.API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 60000, // Default timeout of 60 seconds
});

// Custom timeout configurations
const getRequestConfig = {
  // Increase timeout for initial folder load and sync operations
  folder: () => ({
    timeout: 180000, // 3 minutes timeout for folder operations
  }),
  // Increase timeout for email move operations
  move: () => ({
    timeout: 300000, // 5 minutes timeout for move operations
  })
};

// Request interceptor for API calls
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token to every request
    const token = authService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Apply custom timeouts based on operation type
    if (config.url?.includes('/api/folders/')) {
      config.timeout = getRequestConfig.folder().timeout;
    }
    // Apply move operation timeout
    else if (config.url?.includes('/api/email/move') || config.url?.includes('/api/email/bulk/')) {
      config.timeout = getRequestConfig.move().timeout;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // Handle network errors
    if (!error.response) {
      if (error.code === 'ECONNABORTED') {
        const timeoutError = error as any;
        timeoutError.isTimeout = true;
        return Promise.reject(timeoutError);
      }
      const networkError = error as any;
      networkError.isNetworkError = true;
      return Promise.reject(networkError);
    }

    // Handle token refresh
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        await authService.refreshToken();
        const token = authService.getAccessToken();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return api(originalRequest);
      } catch (refreshError) {
        authService.logout();
        return Promise.reject(refreshError);
      }
    }

    // Handle other error cases
    if (error.response.status === 403) {
      authService.logout();
    }

    if (error.response.status === 404) {
      // Handle 404
    }

    if (error.response.status >= 500) {
      // Handle server errors
    }

    return Promise.reject(error);
  }
);

// Utility function to handle API errors consistently
export const handleApiError = (error: any): string => {
  // Handle timeout errors
  if (error.isTimeout || error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Request timed out. The server is taking too long to respond. Please try again.';
  }

  // Handle network errors
  if (error.isNetworkError || !error.response) {
    return 'Network error. Please check your internet connection and try again.';
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  if (error.response?.status) {
    switch (error.response.status) {
      case 400:
        return 'Bad request - please check your input';
      case 401:
        return 'Unauthorized - please log in again';
      case 403:
        return 'Access forbidden - insufficient permissions';
      case 404:
        return 'Resource not found';
      case 500:
        return 'Server error - please try again later';
      case 502:
        return 'Bad gateway - server is temporarily unavailable';
      case 503:
        return 'Service unavailable - server is under maintenance';
      case 504:
        return 'Gateway timeout - server is taking too long to respond';
      default:
        return `Request failed with status ${error.response.status}`;
    }
  }
  
  return 'An unexpected error occurred';
};

// Enhanced email API with better error handling
export const emailApi = {
  // Fetch emails with pagination and filters
  getEmails: async (params: {
    category?: string;
    page?: number;
    limit?: number;
    search?: string;
  }) => {
    try {
      const response = await api.get('/api/email/emails', { params });
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Fetch emails for a category
  getEmailsForCategory: async (categoryId: string, page: number = 1, limit: number = 20) => {
    try {
      const response = await api.get(`/api/email/categories/${categoryId}/messages?page=${page}&limit=${limit}`);
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Fetch email categories
  getCategories: async () => {
    try {
      const response = await api.get('/api/email/categories');
      return response.data;
    } catch (error) {
      throw handleApiError(error);
    }
  },

  // Get a single email by ID
  getEmail: async (id: string) => {
    try {
      const { data } = await api.get<Email>(`/emails/${id}`);
      return data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Send a new email
  sendEmail: async (email: Omit<Email, 'id' | 'date' | 'isRead' | 'isStarred'>) => {
    try {
      const { data } = await api.post<Email>('/emails', email);
      return data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Update email (mark as read, star, etc.)
  updateEmail: async (id: string, updates: Partial<Email>) => {
    try {
      const { data } = await api.patch<Email>(`/emails/${id}`, updates);
      return data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Delete an email
  deleteEmail: async (id: string) => {
    try {
      await api.delete(`/emails/${id}`);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Upload attachment
  uploadAttachment: async (emailId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post(`/emails/${emailId}/attachments`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },
};

// Star or unstar an email
export const starEmail = async (folder: string, uid: string | number, id: string, starred: boolean) => {
  try {
    const { data } = await api.put(`/api/email/message/${folder}/${id}/${uid}/star`, { starred });

    //add a toast to the user
    if (starred) {
      toast.success('Email starred');
    } else {
      toast.success('Email unstarred');
    }

    return data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

export const moveEmails = async ({ sourceFolder, targetFolder, uids, copy = false }: {
  sourceFolder: string;
  targetFolder: string;
  uids: (number | string)[];
  copy?: boolean;
}) => {
  return api.post('/api/email/move?implementation=node-imap', {
    sourceFolder,
    targetFolder,
    uids,
    copy,
  });
};

export const bulkStarEmails = async ({ folderPath, uids, starred }: {
  folderPath: string;
  uids: (number | string)[];
  starred: boolean;
}) => {
  return api.post('/api/email/bulk/star?implementation=node-imap', {
    folderPath,
    uids,
    starred,
  });
};

export const bulkMarkReadEmails = async ({ folderPath, uids, read }: {
  folderPath: string;
  uids: (number | string)[];
  read: boolean;
}) => {
  return api.post('/api/email/bulk/mark-read?implementation=node-imap', {
    folderPath,
    uids,
    read,
  });
};

export const bulkDeleteEmails = async ({ folderPath, uids }: {
  folderPath: string;
  uids: (number | string)[];
}) => {
  return api.post('/api/email/bulk/delete?implementation=node-imap', {
    folderPath,
    uids,
  });
};

export const deleteEmail = async (folder: string, emailId: string, uid: number | string) => {
  try {
    const { data } = await api.delete(`/api/email/message/${folder}/${emailId}/${uid}/delete`);
    
    if (data.success) {
      toast.success('Email moved to trash');
    }
    
    return data;
  } catch (error) {
    throw new Error(handleApiError(error));
  }
};

// Email address search
export const searchEmailAddresses = async (query: string, limit: number = 10) => {
  try {
    const response = await api.get(`/api/email/search/addresses?q=${query}&limit=${limit}`);
    return response.data;
  } catch (error) {
    console.error('Error searching email addresses:', error);
    return [];
  }
};

// Add syncFolders function
export const syncFolders = async () => {
  try {
    const response = await api.get('/api/folders/sync');
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export const bulkMarkEmailsRead = async (folderPath: string, emailIds: string[]) => {
  try {
    const response = await api.post('/api/email/batch', { folderPath, emailIds });
    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
};

export default api; 